// src/components/Register.js - Enhanced UX with Helper Integration
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { sendCustomVerificationEmail, generateVerificationToken } from '../services/emailService';
import './Register.css';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('pending'); // 'pending', 'checking', 'verified'
  const [pollingInterval, setPollingInterval] = useState(null);

  const { register, error, setError } = useAuth();

  // Email verification polling helper
  const startVerificationPolling = () => {
    console.log('📧 Starting verification polling...');
    
    const interval = setInterval(async () => {
      try {
        // Check if user has verified their email
        // This would need a backend endpoint to check verification status
        // For now, we'll simulate it or use Firebase Auth state changes
        console.log('🔍 Polling verification status...');
        
        // TODO: Implement actual verification status check
        // const status = await checkVerificationStatus(formData.email);
        // if (status.verified) {
        //   setVerificationStatus('verified');
        //   clearInterval(interval);
        // }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 5000); // Check every 5 seconds

    setPollingInterval(interval);

    // Clear interval after 10 minutes
    setTimeout(() => {
      if (interval) {
        clearInterval(interval);
        setPollingInterval(null);
      }
    }, 600000); // 10 minutes
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Naam is verplicht';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is verplicht';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Ongeldig email adres';
    }

    if (!formData.password) {
  errors.password = 'Wachtwoord is verplicht';
} else if (formData.password.length < 12) {
  errors.password = 'Wachtwoord moet minimaal 12 karakters zijn';
} else if (!/[A-Z]/.test(formData.password)) {
  errors.password = 'Wachtwoord moet minimaal 1 hoofdletter bevatten';
} else if (!/[a-z]/.test(formData.password)) {
  errors.password = 'Wachtwoord moet minimaal 1 kleine letter bevatten';
} else if (!/[0-9]/.test(formData.password)) {
  errors.password = 'Wachtwoord moet minimaal 1 cijfer bevatten';
} else if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
  errors.password = 'Wachtwoord moet minimaal 1 speciaal teken bevatten (!@#$%^&* etc.)';
}

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Wachtwoorden komen niet overeen';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsLoading(true);
    setValidationErrors({});

    try {
      console.log('🚀 Starting enhanced registration process...');

      // Step 1: Create Firebase user (without auto email verification)
      const result = await register(formData.email, formData.password, formData.name, { skipEmailVerification: true });
      
      if (result.success) {
        console.log('✅ Firebase user created successfully');

        // Step 2: Send custom verification email via Brevo
        const verificationToken = generateVerificationToken();
        const emailResult = await sendCustomVerificationEmail(
          formData.email, 
          formData.name, 
          verificationToken
        );

        if (emailResult.success) {
          console.log('✅ Custom verification email sent via Brevo');
          setRegistrationSuccess(true);
          setSuccessMessage('Account aangemaakt! Check je email voor verificatie.');
          
          // Start polling for verification
          startVerificationPolling();
        } else {
          console.error('❌ Custom email failed, falling back to Firebase');
          // Fallback: Let Firebase handle email verification
          setRegistrationSuccess(true);
          setSuccessMessage('Account aangemaakt! Check je email voor verificatie.');
        }
      } else {
        console.error('❌ Registration failed:', result.error);
      }
    } catch (err) {
      console.error('🚨 Registration exception:', err);
    }
    
    setIsLoading(false);
  };

  const handleResendVerification = async () => {
    try {
      console.log('📧 Resending verification email...');
      
      const verificationToken = generateVerificationToken();
      const result = await sendCustomVerificationEmail(
        formData.email, 
        formData.name, 
        verificationToken
      );
      
      if (result.success) {
        setSuccessMessage('Verificatie email opnieuw verzonden!');
      } else {
        setError('Kon verificatie email niet versturen. Probeer het later opnieuw.');
      }
    } catch (error) {
      console.error('Resend error:', error);
      setError('Er is een fout opgetreden bij het versturen van de email.');
    }
  };

  const getOrganizationPreview = () => {
    if (formData.email) {
      const domain = formData.email.split('@')[1];
      if (domain) {
        return `org_${domain.replace(/\./g, '_')}`;
      }
    }
    // return 'Je organisatie ID wordt automatisch bepaald';
  };

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <h1>Account Aanmaken</h1>
          <p>Maak een account aan voor je RelatieScan</p>
        </div>

        <div className="register-content">
          {error && (
            <div className="error-state">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="success-state">
              {successMessage}
            </div>
          )}

          {registrationSuccess ? (
            <div className="success-screen">
              <div className="success-icon">
                📧
              </div>

              <h2 className="success-title">Account Aangemaakt!</h2>

              <p className="success-description">
                We hebben een verificatie email gestuurd naar <strong>{formData.email}</strong>.
                Klik op de link in je email om je account te activeren.
              </p>

              {/* Enhanced Progress Checklist */}
              <div className="email-status-checklist">
                <h3 className="email-status-title">Verificatie Status</h3>
                
                <div className="status-item">
                  <div className="status-icon completed">✓</div>
                  <span>Account aangemaakt</span>
                </div>
                
                <div className="status-item">
                  <div className="status-icon completed">✓</div>
                  <span>Verificatie email verzonden</span>
                </div>
                
                <div className="status-item">
                  <div className={`status-icon ${verificationStatus === 'verified' ? 'completed' : verificationStatus === 'checking' ? 'pending' : 'waiting'}`}>
                    {verificationStatus === 'verified' ? '✓' : verificationStatus === 'checking' ? '⏳' : '○'}
                  </div>
                  <span>Email verificatie {verificationStatus === 'verified' ? 'voltooid' : 'in afwachting'}</span>
                </div>
              </div>

              {/* Verification Polling Indicator 
              {pollingInterval && verificationStatus !== 'verified' && (
                <div className="verification-polling">
                  <div className="polling-spinner"></div>
                  <span className="verification-polling-text">
                    We controleren automatisch of je je email hebt geverifieerd...
                  </span>
                </div>
              )} */}

              {/* Tips */}
              <div className="tip-box">
                <div className="tip-box-title">💡 Tip:</div>
                <div className="tip-box-text">
                  Check ook je spam/junk folder als je de email niet ziet.
                </div>
              </div>

              <div className="action-links">
                <button onClick={handleResendVerification} className="btn-secondary">
                  📧 Verificatie Email Opnieuw Versturen
                </button>
                
                <Link to="/login" className="action-link primary">
                  Ga naar Inloggen →
                </Link>
              </div>
            </div>
          ) : (
            <div>
              {/* Progress Steps */}
              <div className="progress-steps">
                <div className="progress-step active">
                  <div className="progress-step-icon">1</div>
                  <span>Gegevens invullen</span>
                </div>
                <div className="progress-step">
                  <div className="progress-step-icon">2</div>
                  <span>Email verifiëren</span>
                </div>
                <div className="progress-step">
                  <div className="progress-step-icon">3</div>
                  <span>Inloggen</span>
                </div>
              </div>

             {/* Organization Preview 
             <div className="organization-preview">
               <div className="organization-preview-label">Organisatie:</div>
              <div className="organization-preview-value">{getOrganizationPreview()}</div>
              </div> */}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">
                    Volledige naam
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-input ${validationErrors.name ? 'error' : ''}`}
                    placeholder="Je voor- en achternaam"
                  />
                  {validationErrors.name && (
                    <div className="error-message">{validationErrors.name}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Email adres (werk email)
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-input ${validationErrors.email ? 'error' : ''}`}
                    placeholder="naam@bedrijf.nl"
                  />
                  {validationErrors.email && (
                    <div className="error-message">{validationErrors.email}</div>
                  )}
                  <div className="help-text">
                    Gebruik je zakelijke email adres voor teamtoegang
                  </div>
                </div>

                <div className="form-group">
  <label className="form-label">
    Wachtwoord
  </label>
  <input
    type="password"
    name="password"
    value={formData.password}
    onChange={handleChange}
    className={`form-input ${validationErrors.password ? 'error' : ''}`}
    placeholder="Minimaal 12 karakters"
  />
  <div className="help-text">
    Je wachtwoord moet voldoen aan:
    <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
      <li>Minimaal 12 karakters</li>
      <li>Minimaal 1 hoofdletter (A-Z)</li>
      <li>Minimaal 1 kleine letter (a-z)</li>
      <li>Minimaal 1 cijfer (0-9)</li>
      <li>Minimaal 1 speciaal teken (!@#$%^&*)</li>
    </ul>
  </div>
  {validationErrors.password && (
    <div className="error-message">{validationErrors.password}</div>
  )}
</div>

                <div className="form-group">
                  <label className="form-label">
                    Wachtwoord bevestigen
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`form-input ${validationErrors.confirmPassword ? 'error' : ''}`}
                    placeholder="Herhaal je wachtwoord"
                  />
                  {validationErrors.confirmPassword && (
                    <div className="error-message">{validationErrors.confirmPassword}</div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary"
                >
                  {isLoading ? 'Account aanmaken...' : 'Account Aanmaken'}
                </button>
              </form>
            </div>
          )}
        </div>

        <div className="register-footer">
          <p className="register-footer-text">
            Al een account?{' '}
            <Link to="/login" className="register-footer-link">
              Inloggen
            </Link>
          </p>
          <p className="register-footer-text">
            <Link to="/" className="register-footer-link">
              ← Terug naar homepage
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;